import { useEffect, useState, useRef } from 'react';
import { useAuthContext } from './components/AuthProvider';
import posthog from './lib/posthog';
import { trackMetaEvent } from './lib/meta';
import { getAdAttribution } from './lib/adAttribution';
import { sendCAPIEvent } from './lib/api';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import {
  getBillingUsage, getDailyBillingUsage, getCreditBalance, redeemCoupon,
  getMyOrg, getOrgMembers, createOrgInvite, updateOrg,
  resendOrgInvite, revokeOrgInvite, removeOrgMember,
  getCreditSettings, updateCreditSettings, getCreditHistory, switchToCredits, confirmCreditPurchase,
  getWinbackStatus, getReferralCode, sendReferralInvite,
  getSubscription, cancelSubscription, changePlan, subscribe, confirmSubscription,
} from './lib/api';
import BuyCreditsModal from './components/BuyCreditsModal';
import PaymentMethodsPanel from './components/PaymentMethodsPanel';
import OrganizationIcon from './components/OrganizationIcon';
import { formatUsdCents as formatCurrency } from './utils/currency';
import './AccountPage.css';

function formatOrganizationDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
}

function AccountPage() {
  const { user, loading } = useAuthContext();
  
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);
  
  const [dailyUsage, setDailyUsage] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);

  const [orgInfo, setOrgInfo] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [orgInvites, setOrgInvites] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [orgMessage, setOrgMessage] = useState(null);
  const [orgActionId, setOrgActionId] = useState(null);
  const [memberRemovalId, setMemberRemovalId] = useState(null);
  
  // Org name editing state
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [editedOrgName, setEditedOrgName] = useState('');
  const [orgNameSaving, setOrgNameSaving] = useState(false);
  
  // Credits & coupon state
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditLoading, setCreditLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponSubmitting, setCouponSubmitting] = useState(false);
  const [couponMessage, setCouponMessage] = useState(null); // { type: 'success'|'error', text }

  // Credit settings & history
  const [creditSettings, setCreditSettings] = useState(null);
  const [creditHistory, setCreditHistory] = useState(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [autoReloadSaving, setAutoReloadSaving] = useState(false);
  const [switchingToCredits, setSwitchingToCredits] = useState(false);
  
  // Winback email preferences
  const [winbackStatus, setWinbackStatus] = useState(null);

  // Referral state
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [referralInviteEmail, setReferralInviteEmail] = useState('');
  const [referralInviteSending, setReferralInviteSending] = useState(false);
  const [referralInviteMessage, setReferralInviteMessage] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subAction, setSubAction] = useState(null); // 'cancelling' | 'changing' | 'subscribing'

  // Tooltip state
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, date: '', amount: '' });
  
  // Track if we've already fetched data for this user
  const hasFetchedRef = useRef(false);
  const fetchedUserIdRef = useRef(null);
  const fetchedOrgUserIdRef = useRef(null);

  // Fetch usage data when user is available (only once per user)
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    
    // Skip if we've already fetched for this user
    if (hasFetchedRef.current && fetchedUserIdRef.current === userId) {
      return;
    }
    
    const fetchUsage = async () => {
      try {
        setUsageLoading(true);
        setUsageError(null);
        const data = await getBillingUsage();
        setUsage(data);
      } catch (err) {
        console.error('Failed to fetch usage:', err);
        setUsageError('We could not load your usage right now. Please try again.');
      } finally {
        setUsageLoading(false);
      }
    };
    
    const fetchDailyUsage = async () => {
      try {
        setDailyLoading(true);
        const data = await getDailyBillingUsage();
        setDailyUsage(data);
      } catch (err) {
        console.error('Failed to fetch daily usage:', err);
      } finally {
        setDailyLoading(false);
      }
    };
    
    const fetchCredits = async () => {
      try {
        setCreditLoading(true);
        const data = await getCreditBalance();
        setCreditBalance(data);
      } catch (err) {
        console.error('Failed to fetch credits:', err);
      } finally {
        setCreditLoading(false);
      }
    };

    const fetchCreditSettings = async () => {
      try {
        const data = await getCreditSettings();
        setCreditSettings(data);
      } catch (err) {
        console.error('Failed to fetch credit settings:', err);
      }
    };

    const fetchCreditHistory = async () => {
      try {
        const data = await getCreditHistory(20, 0);
        setCreditHistory(data);
      } catch (err) {
        console.error('Failed to fetch credit history:', err);
      }
    };

    // Mark as fetched
    hasFetchedRef.current = true;
    fetchedUserIdRef.current = userId;
    
    const fetchWinback = async () => {
      try {
        const data = await getWinbackStatus();
        setWinbackStatus(data);
      } catch (err) {
        // Non-critical — silently ignore
      }
    };

    const fetchReferral = async () => {
      try {
        setReferralLoading(true);
        const data = await getReferralCode();
        setReferralData(data);
      } catch (err) {
        console.error('Failed to fetch referral data:', err);
      } finally {
        setReferralLoading(false);
      }
    };

    const fetchSubscription = async () => {
      try {
        setSubLoading(true);
        const data = await getSubscription();
        setSubscription(data);
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
      } finally {
        setSubLoading(false);
      }
    };

    // Fetch all in parallel
    fetchUsage();
    fetchDailyUsage();
    fetchCredits();
    fetchCreditSettings();
    fetchCreditHistory();
    fetchWinback();
    fetchReferral();
    fetchSubscription();
  }, [user?.id]);

  useEffect(() => {
    const handleStripeReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const billingStatus = urlParams.get('billing');
      const creditsStatus = urlParams.get('credits');
      const subscriptionStatus = urlParams.get('subscription');
      const sessionId = urlParams.get('session_id');

      if (!billingStatus && !creditsStatus && !subscriptionStatus) return;

      if (creditsStatus === 'success' && sessionId) {
        try {
          const result = await confirmCreditPurchase(sessionId);
          // Deterministic event_id for dedup with CAPI
          const purchaseValue = result?.creditedCents || result?.amountCents;
          if (purchaseValue) {
            trackMetaEvent('Purchase', {
              value: purchaseValue / 100,
              currency: 'USD',
              content_type: 'credits',
              order_id: sessionId,
              num_items: 1,
            }, `purchase_${sessionId}`);
          }
        } catch (err) {
          console.error('Failed to confirm credit purchase:', err);
        }
      }

      if (subscriptionStatus === 'success' && sessionId) {
        try {
          const result = await confirmSubscription(sessionId);
          console.log('Subscription confirmed:', result);
          // Fire Purchase + Subscribe pixel events with deterministic event_id
          const tier = result?.tier || result?.subscription_tier;
          const subValue = tier === 'studio' ? 200 : 20;
          trackMetaEvent('Purchase', {
            value: subValue,
            currency: 'USD',
            content_type: 'subscription',
            order_id: sessionId,
            num_items: 1,
          }, `purchase_${sessionId}`);
          trackMetaEvent('Subscribe', {
            value: subValue,
            currency: 'USD',
          });
          // Refresh subscription data
          const subData = await getSubscription();
          setSubscription(subData);
        } catch (err) {
          console.error('Failed to confirm subscription:', err);
        }
      }

      if (billingStatus === 'success' || creditsStatus === 'success') {
        try {
          setUsageLoading(true);
          setDailyLoading(true);
          setCreditLoading(true);

          const [usageData, dailyData, creditData, settingsData, historyData] = await Promise.all([
            getBillingUsage(),
            getDailyBillingUsage(),
            getCreditBalance(),
            getCreditSettings(),
            getCreditHistory(20, 0),
          ]);

          setUsage(usageData);
          setDailyUsage(dailyData);
          setCreditBalance(creditData);
          setCreditSettings(settingsData);
          setCreditHistory(historyData);
          hasFetchedRef.current = true;
          if (user?.id) {
            fetchedUserIdRef.current = user.id;
          }
        } catch (err) {
          console.error('Failed to refresh account data after checkout:', err);
        } finally {
          setUsageLoading(false);
          setDailyLoading(false);
          setCreditLoading(false);
        }
      }

      window.history.replaceState({}, '', window.location.pathname);
    };

    handleStripeReturn();
  }, [user?.id]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    if (fetchedOrgUserIdRef.current === userId) return;
    fetchedOrgUserIdRef.current = userId;

    const loadOrg = async () => {
      try {
        setOrgLoading(true);
        setOrgError(null);
        const orgRes = await getMyOrg();
        const org = orgRes?.organization || null;
        setOrgInfo(org);

        if (org) {
          const membersRes = await getOrgMembers();
          setOrgMembers(membersRes?.members || []);
          setOrgInvites((membersRes?.invites || []).filter((i) => i.status !== 'accepted'));
        } else {
          setOrgMembers([]);
          setOrgInvites([]);
        }
      } catch (err) {
        setOrgError('Failed to load organization.');
      } finally {
        setOrgLoading(false);
      }
    };

    loadOrg();
  }, [user?.id]);

  const applyOrgRoster = (membersRes) => {
    setOrgInfo(membersRes?.organization || orgInfo);
    setOrgMembers(membersRes?.members || []);
    setOrgInvites((membersRes?.invites || []).filter((invite) => invite.status !== 'accepted'));
  };

  const refreshOrgRoster = async () => {
    const membersRes = await getOrgMembers();
    applyOrgRoster(membersRes);
    return membersRes;
  };

  const retryOrganizationLoad = async () => {
    setOrgLoading(true);
    setOrgError(null);
    try {
      const orgRes = await getMyOrg();
      const org = orgRes?.organization || null;
      setOrgInfo(org);
      if (org) await refreshOrgRoster();
    } catch (err) {
      setOrgError('Failed to load organization.');
    } finally {
      setOrgLoading(false);
    }
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setOrgMessage(null);
    if (!inviteEmail.trim()) return;

    try {
      setInviteSending(true);
      await createOrgInvite({
        email: inviteEmail.trim(),
        redirectUrl: `${window.location.origin}/invite/accept`,
      });
      setInviteEmail('');
      setOrgMessage({ type: 'success', text: `Invitation sent to ${inviteEmail.trim()}.` });
      await refreshOrgRoster();
    } catch (err) {
      setOrgMessage({ type: 'error', text: 'Failed to send invitation.' });
    } finally {
      setInviteSending(false);
    }
  };

  const handleInviteAction = async (invite, action) => {
    setOrgActionId(`${action}:${invite.id}`);
    setOrgMessage(null);
    try {
      if (action === 'resend') {
        await resendOrgInvite(invite.id);
        setOrgMessage({ type: 'success', text: `Invitation resent to ${invite.email}.` });
      } else {
        await revokeOrgInvite(invite.id);
        setOrgMessage({ type: 'success', text: `Invitation to ${invite.email} revoked.` });
      }
      await refreshOrgRoster();
    } catch (err) {
      setOrgMessage({ type: 'error', text: `Failed to ${action} invitation.` });
    } finally {
      setOrgActionId(null);
    }
  };

  const handleRemoveMember = async (member) => {
    setOrgActionId(`remove:${member.userId}`);
    setOrgMessage(null);
    try {
      await removeOrgMember(member.userId);
      setMemberRemovalId(null);
      setOrgMessage({
        type: 'success',
        text: `${member.displayName || member.email || 'Member'} was removed from the organization.`,
      });
      await refreshOrgRoster();
    } catch (err) {
      setOrgMessage({ type: 'error', text: 'Failed to remove member.' });
    } finally {
      setOrgActionId(null);
    }
  };

  const handleEditOrgName = () => {
    setEditedOrgName(orgInfo?.name || '');
    setIsEditingOrgName(true);
    setOrgMessage(null);
  };

  const handleCancelEditOrgName = () => {
    setIsEditingOrgName(false);
    setEditedOrgName('');
  };

  const handleSaveOrgName = async () => {
    const trimmedName = editedOrgName.trim();
    if (!trimmedName) {
      setOrgMessage({ type: 'error', text: 'Organization name cannot be empty.' });
      return;
    }
    if (trimmedName === orgInfo?.name) {
      setIsEditingOrgName(false);
      return;
    }

    try {
      setOrgNameSaving(true);
      setOrgMessage(null);
      const result = await updateOrg({ name: trimmedName });
      setOrgInfo(result.organization);
      setIsEditingOrgName(false);
      setOrgMessage({ type: 'success', text: 'Organization name updated.' });
    } catch (err) {
      setOrgMessage({ type: 'error', text: 'Failed to update organization name.' });
    } finally {
      setOrgNameSaving(false);
    }
  };

  const handleOrgNameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveOrgName();
    } else if (event.key === 'Escape') {
      handleCancelEditOrgName();
    }
  };

  const handleCouponSubmit = async (event) => {
    event.preventDefault();
    const trimmed = couponCode.trim();
    if (!trimmed) return;

    try {
      setCouponSubmitting(true);
      setCouponMessage(null);
      const result = await redeemCoupon(trimmed);
      posthog.capture('coupon redeemed', { credited_cents: result.creditedCents });
      setCouponCode('');
      setCreditBalance({
        creditBalanceCents: result.newBalanceCents,
        creditBalanceDollars: result.newBalanceDollars,
      });
      setCouponMessage({
        type: 'success',
        text: `${formatCurrency(result.creditedCents)} added to your balance`,
      });
    } catch (err) {
      setCouponMessage({
        type: 'error',
        text: 'Failed to redeem coupon',
      });
    } finally {
      setCouponSubmitting(false);
    }
  };

  const handleAutoReloadToggle = async (enabled) => {
    if (!creditSettings) return;
    setAutoReloadSaving(true);
    try {
      const result = await updateCreditSettings({
        autoReloadEnabled: enabled,
        autoReloadThresholdCents: creditSettings.autoReloadThresholdCents || 500,
        autoReloadAmountCents: creditSettings.autoReloadAmountCents || 2500,
      });
      setCreditSettings(result);
    } catch (err) {
      console.error('Failed to update auto-reload:', err);
    } finally {
      setAutoReloadSaving(false);
    }
  };

  const handleAutoReloadSettingsSave = async (thresholdCents, amountCents) => {
    setAutoReloadSaving(true);
    try {
      const result = await updateCreditSettings({
        autoReloadEnabled: creditSettings?.autoReloadEnabled ?? false,
        autoReloadThresholdCents: thresholdCents,
        autoReloadAmountCents: amountCents,
      });
      setCreditSettings(result);
    } catch (err) {
      console.error('Failed to update auto-reload settings:', err);
    } finally {
      setAutoReloadSaving(false);
    }
  };

  const handleSwitchToCredits = async () => {
    if (!window.confirm('Switch to credit-based billing? Your current PAYG subscription will be cancelled.')) return;
    setSwitchingToCredits(true);
    try {
      const result = await switchToCredits();
      setCreditSettings((prev) => prev ? { ...prev, billingMode: 'credits' } : prev);
      setCreditBalance({
        creditBalanceCents: result.newBalanceCents,
        creditBalanceDollars: result.newBalanceDollars,
      });
      // Re-fetch usage to reflect new mode
      const usageData = await getBillingUsage();
      setUsage(usageData);
    } catch (err) {
      console.error('Failed to switch to credits:', err);
    } finally {
      setSwitchingToCredits(false);
    }
  };

  const handleCopyReferralLink = async () => {
    if (!referralData?.url) return;
    try {
      await navigator.clipboard.writeText(referralData.url);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleReferralInviteSubmit = async (e) => {
    e.preventDefault();
    const trimmed = referralInviteEmail.trim();
    if (!trimmed) return;

    setReferralInviteSending(true);
    setReferralInviteMessage(null);
    try {
      await sendReferralInvite(trimmed);
      setReferralInviteEmail('');
      setReferralInviteMessage({ type: 'success', text: 'Invite sent!' });
    } catch (err) {
      setReferralInviteMessage({ type: 'error', text: 'Failed to send invite.' });
    } finally {
      setReferralInviteSending(false);
    }
  };

  const billingMode = creditSettings?.billingMode || usage?.billingMode || 'payg';
  const canManageBilling = orgInfo?.role !== 'user';

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format short date for chart labels
  const formatShortDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Get max value for chart scaling
  const getMaxDailyUsage = (data) => {
    if (!data?.length) return 0;
    return Math.max(...data.map(d => d.costCents));
  };

  // Tooltip handlers
  const handleBarMouseEnter = (e, day, index) => {
    setTooltip({
      visible: true,
      index: index,
      date: new Date(day.date).toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric' 
      }),
      amount: formatCurrency(day.costCents),
    });
  };

  const handleBarMouseLeave = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  // Fill in the full billing period with zeros for missing days
  const getFullPeriodUsage = () => {
    if (!usage?.periodStart || !usage?.periodEnd) return [];
    
    const startDate = new Date(usage.periodStart);
    const endDate = new Date(usage.periodEnd);
    
    // Create a map of existing usage data by date
    const usageMap = new Map();
    if (dailyUsage?.dailyUsage) {
      dailyUsage.dailyUsage.forEach(day => {
        usageMap.set(day.date, day.costCents);
      });
    }
    
    // Generate all dates in the full billing period
    const fullData = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    
    while (currentDate < endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      fullData.push({
        date: dateStr,
        costCents: usageMap.get(dateStr) || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return fullData;
  };

  return (
    <AuthenticatedLayout>
      <div className="account-content">
        <h1 className="account-title">Settings</h1>
        
        <section className="account-section">
          <h2 className="account-section-title">Profile</h2>
          <div className="account-info-row">
            <span className="account-label">Email</span>
            <span className="account-value">{user?.email || '—'}</span>
          </div>
        </section>

        <section className="account-section account-referral-section" id="referrals">
          <h2 className="account-section-title">Refer & Earn</h2>
          <p className="account-referral-desc">
            Share your link. When a friend signs up and buys $10+ in credits, you both get <strong>$10 free</strong>.
          </p>

          {referralLoading ? (
            <div className="account-usage-loading">Loading referral info...</div>
          ) : referralData ? (
            <>
              <div className="account-referral-link-box">
                <input
                  type="text"
                  value={referralData.url}
                  readOnly
                  className="account-referral-link-input"
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className="account-referral-copy-btn"
                  onClick={handleCopyReferralLink}
                >
                  {referralCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              <div className="account-referral-code-display">
                Your code: <strong>{referralData.code}</strong>
              </div>

              <div className="account-referral-stats">
                <div className="account-referral-stat">
                  <span className="account-referral-stat-value">{referralData.totalReferrals}</span>
                  <span className="account-referral-stat-label">friends referred</span>
                </div>
                <div className="account-referral-stat">
                  <span className="account-referral-stat-value">{formatCurrency(referralData.totalEarnedCents || 0)}</span>
                  <span className="account-referral-stat-label">earned</span>
                </div>
                <div className="account-referral-stat">
                  <span className="account-referral-stat-value">{referralData.pendingReferrals}</span>
                  <span className="account-referral-stat-label">pending</span>
                </div>
              </div>

              <form className="account-referral-invite-form" onSubmit={handleReferralInviteSubmit}>
                <input
                  type="email"
                  value={referralInviteEmail}
                  onChange={(e) => {
                    setReferralInviteEmail(e.target.value);
                    if (referralInviteMessage) setReferralInviteMessage(null);
                  }}
                  placeholder="Friend's email address"
                  disabled={referralInviteSending}
                />
                <button type="submit" disabled={referralInviteSending || !referralInviteEmail.trim()}>
                  {referralInviteSending ? 'Sending...' : 'Send invite'}
                </button>
              </form>

              {referralInviteMessage && (
                <div className={`account-coupon-message account-coupon-message-${referralInviteMessage.type}`}>
                  {referralInviteMessage.text}
                </div>
              )}

              {referralData.referrals && referralData.referrals.length > 0 && (
                <div className="account-org-list" style={{ marginTop: '16px' }}>
                  <div className="account-org-list-title">Referrals</div>
                  {referralData.referrals.map((ref, idx) => (
                    <div key={idx} className="account-org-list-row">
                      <span>Friend #{idx + 1}</span>
                      <span className={`account-referral-badge account-referral-badge--${ref.status}`}>
                        {ref.status === 'credited' ? '$10 earned' : 'pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </section>

        {/* Subscription Management */}
        <section className="account-section">
          <h2 className="account-section-title">Subscription</h2>
          {!canManageBilling && (
            <p className="account-billing-readonly">Plan changes are managed by your organization owner.</p>
          )}
          {subLoading ? (
            <p className="account-loading">Loading subscription...</p>
          ) : (
            <div className="subscription-plans">
              {/* Pay As You Go Card */}
              <article className={`subscription-plan-card${!subscription?.tier ? ' subscription-plan-card--active' : ''}`}>
                {!subscription?.tier && <div className="subscription-plan-card__badge">Current</div>}
                <h3 className="subscription-plan-card__name">Pay As You Go</h3>
                <div className="subscription-plan-card__price">
                  <span className="subscription-plan-card__amount">$0</span>
                  <span className="subscription-plan-card__period">/mo</span>
                </div>
                <ul className="subscription-plan-card__features">
                  <li>Per-model video pricing</li>
                  <li>Per-model image pricing</li>
                  <li>No monthly commitment</li>
                </ul>
                {!subscription?.tier ? (
                  <button className="subscription-plan-card__action subscription-plan-card__action--current" disabled>
                    Current Plan
                  </button>
                ) : (
                  <button
                    className="subscription-plan-card__action subscription-plan-card__action--secondary"
                    disabled={!canManageBilling || !!subAction || subscription.cancelAtPeriodEnd}
                    onClick={async () => {
                      if (!window.confirm('Cancel your subscription? It will remain active until the end of the current billing period.')) return;
                      setSubAction('cancelling');
                      try {
                        await cancelSubscription();
                        posthog.capture('subscription cancelled');
                        const data = await getSubscription();
                        setSubscription(data);
                      } catch (err) {
                        alert('Failed to cancel subscription');
                      } finally {
                        setSubAction(null);
                      }
                    }}
                  >
                    {subAction === 'cancelling' ? 'Cancelling...' : subscription.cancelAtPeriodEnd ? 'Returning after period' : 'Switch to Free'}
                  </button>
                )}
              </article>

              {/* Creator Card */}
              <article className={`subscription-plan-card${subscription?.tier === 'creator' ? ' subscription-plan-card--active' : ''}`}>
                {subscription?.tier === 'creator' && <div className="subscription-plan-card__badge">Current</div>}
                <h3 className="subscription-plan-card__name">Creator</h3>
                <div className="subscription-plan-card__price">
                  <span className="subscription-plan-card__amount">$20</span>
                  <span className="subscription-plan-card__period">/mo</span>
                </div>
                <ul className="subscription-plan-card__features">
                  <li><strong>$30/mo video allowance</strong></li>
                  <li><strong>Unlimited images</strong></li>
                  <li><strong>Unlimited voice</strong></li>
                  <li>PAYG after allowance</li>
                </ul>
                {/* Active plan details */}
                {subscription?.tier === 'creator' && (
                  <>
                    <div className="subscription-plan-card__details">
                      {subscription.periodStart && (
                        <div className="subscription-plan-card__detail-row">
                          <span className="subscription-plan-card__detail-label">Period</span>
                          <span className="subscription-plan-card__detail-value">
                            {new Date(subscription.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(subscription.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      )}
                      {subscription.videoAllowanceCents != null && (
                        <>
                          <div className="subscription-plan-card__detail-row">
                            <span className="subscription-plan-card__detail-label">Allowance</span>
                            <span className="subscription-plan-card__detail-value">
                              ${(subscription.videoAllowanceUsedCents / 100).toFixed(2)} / ${(subscription.videoAllowanceCents / 100).toFixed(2)}
                            </span>
                          </div>
                          <div className="subscription-plan-card__allowance-bar">
                            <div
                              className="subscription-plan-card__allowance-fill"
                              style={{ width: `${Math.min(100, (subscription.videoAllowanceUsedCents / subscription.videoAllowanceCents) * 100)}%` }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {subscription.cancelAtPeriodEnd && (
                      <p className="subscription-plan-card__cancel-notice">Ends after current period</p>
                    )}
                  </>
                )}
                {/* Action button */}
                {subscription?.tier === 'creator' ? (
                  <button className="subscription-plan-card__action subscription-plan-card__action--current" disabled>
                    Current Plan
                  </button>
                ) : subscription?.tier === 'studio' ? (
                  <button
                    className="subscription-plan-card__action subscription-plan-card__action--secondary"
                    disabled={!canManageBilling || !!subAction || subscription.cancelAtPeriodEnd}
                    onClick={async () => {
                      setSubAction('changing');
                      try {
                        await changePlan('creator');
                        posthog.capture('plan changed', { new_tier: 'creator' });
                        const data = await getSubscription();
                        setSubscription(data);
                      } catch (err) {
                        alert('Failed to change plan');
                      } finally {
                        setSubAction(null);
                      }
                    }}
                  >
                    {subAction === 'changing' ? 'Changing...' : 'Downgrade'}
                  </button>
                ) : (
                  <button
                    className="subscription-plan-card__action subscription-plan-card__action--primary"
                    disabled={!canManageBilling || !!subAction}
                    onClick={async () => {
                      setSubAction('subscribing');
                      try {
                        const creatorAttrib = getAdAttribution();
                        const creatorEventId = trackMetaEvent('InitiateCheckout', { value: 20, currency: 'USD', content_type: 'subscription', num_items: 1 });
                        sendCAPIEvent('InitiateCheckout', creatorEventId, { value: 20, currency: 'USD', num_items: 1 }, creatorAttrib).catch(() => {});
                        const result = await subscribe('creator', `${window.location.origin}/account?subscription=success&session_id={CHECKOUT_SESSION_ID}`, `${window.location.origin}/account`, { eventId: creatorEventId, fbc: creatorAttrib.fbc, fbp: creatorAttrib.fbp });
                        if (result.checkoutUrl) window.location.href = result.checkoutUrl;
                      } catch (err) {
                        alert('Failed to start checkout');
                      } finally {
                        setSubAction(null);
                      }
                    }}
                  >
                    {subAction === 'subscribing' ? 'Loading...' : 'Subscribe'}
                  </button>
                )}
              </article>

              {/* Studio Card */}
              <article className={`subscription-plan-card${subscription?.tier === 'studio' ? ' subscription-plan-card--active' : ''}`}>
                {subscription?.tier === 'studio' && <div className="subscription-plan-card__badge">Current</div>}
                <h3 className="subscription-plan-card__name">Studio</h3>
                <div className="subscription-plan-card__price">
                  <span className="subscription-plan-card__amount">$200</span>
                  <span className="subscription-plan-card__period">/mo</span>
                </div>
                <ul className="subscription-plan-card__features">
                  <li><strong>$500/mo video allowance</strong></li>
                  <li><strong>Unlimited images</strong></li>
                  <li><strong>Unlimited voice</strong></li>
                  <li>Priority processing</li>
                </ul>
                {/* Active plan details */}
                {subscription?.tier === 'studio' && (
                  <>
                    <div className="subscription-plan-card__details">
                      {subscription.periodStart && (
                        <div className="subscription-plan-card__detail-row">
                          <span className="subscription-plan-card__detail-label">Period</span>
                          <span className="subscription-plan-card__detail-value">
                            {new Date(subscription.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(subscription.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      )}
                      {subscription.videoAllowanceCents != null && subscription.videoAllowanceCents < 10000000 && (
                        <>
                          <div className="subscription-plan-card__detail-row">
                            <span className="subscription-plan-card__detail-label">Allowance</span>
                            <span className="subscription-plan-card__detail-value">
                              ${(subscription.videoAllowanceUsedCents / 100).toFixed(2)} / ${(subscription.videoAllowanceCents / 100).toFixed(2)}
                            </span>
                          </div>
                          <div className="subscription-plan-card__allowance-bar">
                            <div
                              className="subscription-plan-card__allowance-fill"
                              style={{ width: `${Math.min(100, (subscription.videoAllowanceUsedCents / subscription.videoAllowanceCents) * 100)}%` }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {subscription.cancelAtPeriodEnd && (
                      <p className="subscription-plan-card__cancel-notice">Ends after current period</p>
                    )}
                  </>
                )}
                {/* Action button */}
                {subscription?.tier === 'studio' ? (
                  <button className="subscription-plan-card__action subscription-plan-card__action--current" disabled>
                    Current Plan
                  </button>
                ) : subscription?.tier === 'creator' ? (
                  <button
                    className="subscription-plan-card__action subscription-plan-card__action--primary"
                    disabled={!canManageBilling || !!subAction || subscription.cancelAtPeriodEnd}
                    onClick={async () => {
                      setSubAction('changing');
                      try {
                        await changePlan('studio');
                        posthog.capture('plan changed', { new_tier: 'studio' });
                        const data = await getSubscription();
                        setSubscription(data);
                      } catch (err) {
                        alert('Failed to change plan');
                      } finally {
                        setSubAction(null);
                      }
                    }}
                  >
                    {subAction === 'changing' ? 'Upgrading...' : 'Upgrade'}
                  </button>
                ) : (
                  <button
                    className="subscription-plan-card__action subscription-plan-card__action--primary"
                    disabled={!canManageBilling || !!subAction}
                    onClick={async () => {
                      setSubAction('subscribing');
                      try {
                        const studioAttrib = getAdAttribution();
                        const studioEventId = trackMetaEvent('InitiateCheckout', { value: 200, currency: 'USD', content_type: 'subscription', num_items: 1 });
                        sendCAPIEvent('InitiateCheckout', studioEventId, { value: 200, currency: 'USD', num_items: 1 }, studioAttrib).catch(() => {});
                        const result = await subscribe('studio', `${window.location.origin}/account?subscription=success&session_id={CHECKOUT_SESSION_ID}`, `${window.location.origin}/account`, { eventId: studioEventId, fbc: studioAttrib.fbc, fbp: studioAttrib.fbp });
                        if (result.checkoutUrl) window.location.href = result.checkoutUrl;
                      } catch (err) {
                        alert('Failed to start checkout');
                      } finally {
                        setSubAction(null);
                      }
                    }}
                  >
                    {subAction === 'subscribing' ? 'Loading...' : 'Subscribe'}
                  </button>
                )}
              </article>
            </div>
          )}
        </section>

        <PaymentMethodsPanel />

        {winbackStatus?.enrolled && (
          <section className="account-section">
            <h2 className="account-section-title">Email Preferences</h2>
            <div className="account-info-row">
              <span className="account-label">Re-engagement emails</span>
              <span className="account-value">
                {winbackStatus.status === 'active' ? 'Active' :
                 winbackStatus.status === 'unsubscribed' ? 'Unsubscribed' :
                 winbackStatus.status === 're_engaged' ? 'Stopped (you came back!)' :
                 'Completed'}
              </span>
            </div>
            {winbackStatus.status === 'active' && (
              <p className="account-email-pref-note">
                You're receiving a short email sequence because we noticed you haven't been back in a while.
                These will stop automatically when you use Latted, or you can{' '}
                <a href="/unsubscribe" className="account-email-pref-link">unsubscribe</a>.
              </p>
            )}
          </section>
        )}

        <section className="account-section account-org-section" id="organization">
          <div className="account-org-heading">
            <div>
              <h2 className="account-section-title">Organization</h2>
              <p>Everyone in your organization can use and manage shared workspace resources.</p>
            </div>
            {orgInfo && <span className="account-org-count">{orgMembers.length} {orgMembers.length === 1 ? 'member' : 'members'}</span>}
          </div>

          {orgLoading ? (
            <div className="account-usage-loading">Loading organization...</div>
          ) : orgError ? (
            <div className="account-org-load-error">
              <span>{orgError}</span>
              <button type="button" onClick={retryOrganizationLoad}>Try again</button>
            </div>
          ) : (
            <>
              <div className="account-org-identity">
                <OrganizationIcon
                  className="account-org-avatar"
                  iconUrl={orgInfo?.iconUrl}
                  name={orgInfo?.name}
                />
                <div className="account-org-identity-copy">
                  {isEditingOrgName ? (
                    <div className="account-org-name-edit">
                      <input
                        type="text"
                        value={editedOrgName}
                        onChange={(e) => setEditedOrgName(e.target.value)}
                        onKeyDown={handleOrgNameKeyDown}
                        placeholder="Organization name"
                        aria-label="Organization name"
                        autoFocus
                        disabled={orgNameSaving}
                      />
                      <button type="button" onClick={handleSaveOrgName} disabled={orgNameSaving} className="account-org-name-save">
                        {orgNameSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" onClick={handleCancelEditOrgName} disabled={orgNameSaving} className="account-org-name-cancel">Cancel</button>
                    </div>
                  ) : (
                    <div className="account-org-name-display">
                      <strong>{orgInfo?.name || 'My Organization'}</strong>
                      {orgInfo?.role === 'owner' && (
                        <button type="button" onClick={handleEditOrgName} className="account-org-name-edit-btn">Rename</button>
                      )}
                    </div>
                  )}
                </div>
                {orgInfo && <span className="account-org-role">{orgInfo.role}</span>}
              </div>

              {(!orgInfo || orgInfo.role === 'owner') && (
                <div className="account-org-invite-panel">
                  <div>
                    <strong>Invite a teammate</strong>
                    <span>Invitations expire after 7 days.</span>
                  </div>
                  <form className="account-org-invite" onSubmit={handleInviteSubmit}>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => { setInviteEmail(event.target.value); setOrgMessage(null); }}
                      placeholder="teammate@company.com"
                      aria-label="Teammate email"
                      autoComplete="email"
                      required
                      disabled={inviteSending}
                    />
                    <button type="submit" disabled={inviteSending || !inviteEmail.trim()}>
                      {inviteSending ? 'Sending...' : 'Send invite'}
                    </button>
                  </form>
                </div>
              )}

              {orgMessage && (
                <div className={`account-org-message account-org-message--${orgMessage.type}`} role={orgMessage.type === 'error' ? 'alert' : 'status'}>
                  {orgMessage.text}
                </div>
              )}

              {orgMembers.length > 0 && (
                <div className="account-org-list">
                  <div className="account-org-list-title">Members</div>
                  {orgMembers.map((member) => {
                    const removing = orgActionId === `remove:${member.userId}`;
                    const confirming = memberRemovalId === member.userId;
                    return (
                      <div key={member.userId} className="account-org-list-row">
                        <div className="account-org-person">
                          <div className="account-org-person-avatar" aria-hidden="true">{(member.displayName || member.email || '?').charAt(0).toUpperCase()}</div>
                          <div>
                            <strong>{member.displayName || member.email || 'Member'}</strong>
                            {member.displayName && member.email && <span>{member.email}</span>}
                          </div>
                        </div>
                        <div className="account-org-row-actions">
                          <span className="account-org-role">{member.role}</span>
                          {orgInfo?.role === 'owner' && member.role !== 'owner' && !confirming && (
                            <button type="button" className="account-org-text-action" onClick={() => setMemberRemovalId(member.userId)}>Remove</button>
                          )}
                          {confirming && (
                            <div className="account-org-confirm">
                              <span>Remove?</span>
                              <button type="button" onClick={() => handleRemoveMember(member)} disabled={removing}>{removing ? 'Removing…' : 'Confirm'}</button>
                              <button type="button" onClick={() => setMemberRemovalId(null)} disabled={removing}>Cancel</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {orgInfo?.role === 'owner' && orgInvites.length > 0 && (
                <div className="account-org-list">
                  <div className="account-org-list-title">Invitations</div>
                  {orgInvites.map((invite) => {
                    const resending = orgActionId === `resend:${invite.id}`;
                    const revoking = orgActionId === `revoke:${invite.id}`;
                    return (
                      <div key={invite.id} className="account-org-list-row">
                        <div className="account-org-person">
                          <div className="account-org-person-avatar account-org-person-avatar--invite" aria-hidden="true">@</div>
                          <div>
                            <strong>{invite.email}</strong>
                            <span>{invite.status === 'pending' ? `Expires ${formatOrganizationDate(invite.expiresAt)}` : `Invite ${invite.status}`}</span>
                          </div>
                        </div>
                        <div className="account-org-row-actions">
                          <span className={`account-org-role account-org-role--${invite.status}`}>{invite.status}</span>
                          <button type="button" className="account-org-text-action" onClick={() => handleInviteAction(invite, 'resend')} disabled={resending || revoking}>
                            {resending ? 'Sending…' : 'Resend'}
                          </button>
                          {invite.status === 'pending' && (
                            <button type="button" className="account-org-text-action account-org-text-action--danger" onClick={() => handleInviteAction(invite, 'revoke')} disabled={resending || revoking}>
                              {revoking ? 'Revoking…' : 'Revoke'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>

        <section className="account-section">
          <h2 className="account-section-title">Credits</h2>

          {creditLoading ? (
            <div className="account-usage-loading">Loading balance...</div>
          ) : (
            <>
              <div className="account-credits-balance">
                <span className="account-credits-value">
                  {formatCurrency(creditBalance?.creditBalanceCents || 0)}
                </span>
                <span className="account-credits-label">credit balance</span>
              </div>

              <div className="account-credits-actions-row">
                <button
                  type="button"
                  className="account-credits-buy-btn"
                  onClick={() => setShowBuyCredits(true)}
                  disabled={!canManageBilling}
                  title={!canManageBilling ? 'Managed by the organization owner' : undefined}
                >
                  Buy credits
                </button>
              </div>

              <form className="account-coupon-form" onSubmit={handleCouponSubmit}>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    if (couponMessage) setCouponMessage(null);
                  }}
                  placeholder="Enter coupon code"
                  disabled={!canManageBilling || couponSubmitting}
                  spellCheck={false}
                  autoComplete="off"
                />
                <button type="submit" disabled={!canManageBilling || couponSubmitting || !couponCode.trim()}>
                  {couponSubmitting ? 'Redeeming...' : 'Redeem'}
                </button>
              </form>

              {couponMessage && (
                <div className={`account-coupon-message account-coupon-message-${couponMessage.type}`}>
                  {couponMessage.text}
                </div>
              )}

              {/* Auto-Reload Settings */}
              {creditSettings && (
                <div className="account-auto-reload">
                  <div className="account-auto-reload-toggle">
                    <label className="account-toggle-label">
                      <input
                        type="checkbox"
                        checked={creditSettings.autoReloadEnabled}
                        onChange={(e) => handleAutoReloadToggle(e.target.checked)}
                        disabled={!canManageBilling || autoReloadSaving}
                      />
                      <span>Auto-reload credits</span>
                    </label>
                    <span className="account-auto-reload-desc">
                      Automatically top up when balance is low
                    </span>
                  </div>

                  {creditSettings.autoReloadEnabled && (
                    <div className="account-auto-reload-config">
                      <div className="account-info-row">
                        <span className="account-label">Reload when below</span>
                        <select
                          value={creditSettings.autoReloadThresholdCents}
                          onChange={(e) =>
                            handleAutoReloadSettingsSave(
                              parseInt(e.target.value, 10),
                              creditSettings.autoReloadAmountCents,
                            )
                          }
                          disabled={!canManageBilling || autoReloadSaving}
                          className="account-auto-reload-select"
                        >
                          <option value={200}>$2</option>
                          <option value={500}>$5</option>
                          <option value={1000}>$10</option>
                          <option value={2000}>$20</option>
                          <option value={5000}>$50</option>
                        </select>
                      </div>
                      <div className="account-info-row">
                        <span className="account-label">Reload amount</span>
                        <select
                          value={creditSettings.autoReloadAmountCents}
                          onChange={(e) =>
                            handleAutoReloadSettingsSave(
                              creditSettings.autoReloadThresholdCents,
                              parseInt(e.target.value, 10),
                            )
                          }
                          disabled={!canManageBilling || autoReloadSaving}
                          className="account-auto-reload-select"
                        >
                          <option value={1000}>$10</option>
                          <option value={2500}>$25</option>
                          <option value={5000}>$50</option>
                          <option value={10000}>$100</option>
                          <option value={25000}>$250</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {/* Switch to Credits - PAYG users only */}
        {billingMode === 'payg' && usage?.hasBilling && canManageBilling && (
          <section className="account-section account-switch-credits">
            <h2 className="account-section-title">Switch to Credit-Based Billing</h2>
            <p className="account-switch-desc">
              Pay upfront for credits instead of being billed at the end of the month. No surprises, full control over your spending.
            </p>
            <button
              type="button"
              className="account-switch-btn"
              onClick={handleSwitchToCredits}
              disabled={switchingToCredits}
            >
              {switchingToCredits ? 'Switching...' : 'Switch to credits'}
            </button>
          </section>
        )}

        {/* Credit Purchase History */}
        {creditHistory && creditHistory.items.length > 0 && (
          <section className="account-section">
            <h2 className="account-section-title">Purchase History</h2>
            <div className="account-credit-history">
              {creditHistory.items.map((item) => (
                <div key={item.id} className="account-credit-history-row">
                  <div className="account-credit-history-info">
                    <span className="account-credit-history-source">{item.source}</span>
                    <span className="account-credit-history-date">
                      {item.createdAt ? formatDate(item.createdAt) : '—'}
                    </span>
                  </div>
                  <span className="account-credit-history-amount">
                    +{formatCurrency(item.amountCents)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="account-section">
          <h2 className="account-section-title">Current Month Charges</h2>
          
          {usageLoading ? (
            <div className="account-usage-loading">Loading usage...</div>
          ) : usageError ? (
            <div className="account-usage-error">
              Unable to load usage data
            </div>
          ) : !usage?.hasBilling ? (
            <div className="account-usage-empty">
              <p>No billing set up yet</p>
              <p className="account-usage-hint">Usage will appear here once you start generating videos.</p>
            </div>
          ) : (
            <>
              <div className="account-usage-amount">
                <span className="account-usage-value">{formatCurrency(usage.totalCostCents)}</span>
                <span className="account-usage-label">current charges</span>
              </div>

              {usage.billedToUserId && usage.billedToUserId !== user?.id && (
                <div className="account-usage-note">
                  Billed to organization owner.
                </div>
              )}
              
              <div className="account-info-row">
                <span className="account-label">Billing Period</span>
                <span className="account-value">
                  {formatDate(usage.periodStart)} — {formatDate(usage.periodEnd)}
                </span>
              </div>
            </>
          )}
        </section>

        {/* Daily Usage Chart - Skeleton */}
        {dailyLoading && (
          <section className="account-section">
            <h2 className="account-section-title">Daily Usage</h2>
            <div className="account-chart">
              <div className="account-chart-bars">
                {Array.from({ length: 30 }).map((_, index) => (
                  <div key={index} className="account-chart-bar-container">
                    <div 
                      className="account-chart-bar account-chart-bar-skeleton"
                      style={{ height: `${20 + Math.random() * 60}%` }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Daily Usage Chart - Loaded */}
        {!dailyLoading && usage?.hasBilling && (() => {
          const fullPeriodData = getFullPeriodUsage();
          if (fullPeriodData.length === 0) return null;
          
          const maxUsage = getMaxDailyUsage(fullPeriodData);
          
          return (
            <section className="account-section">
              <h2 className="account-section-title">Daily Usage</h2>
              {subscription?.tier && (
                <div className="account-usage-info-banner">
                  This is for informational purposes only. See Current Month Charges for actual charges — your subscription allowance will be applied at the end of the month.
                </div>
              )}
              <div className="account-chart">
                <div className="account-chart-bars">
                  {fullPeriodData.map((day, index) => {
                    const heightPercent = maxUsage > 0 ? (day.costCents / maxUsage) * 100 : 0;
                    const hasUsage = day.costCents > 0;
                    const isHovered = tooltip.visible && tooltip.index === index;
                    return (
                      <div key={day.date} className="account-chart-bar-container">
                        {isHovered && (
                          <div className="account-chart-tooltip">
                            <span className="account-chart-tooltip-date">{tooltip.date}</span>
                            <span className="account-chart-tooltip-amount">{tooltip.amount}</span>
                          </div>
                        )}
                        <div 
                          className={`account-chart-bar ${!hasUsage ? 'account-chart-bar-empty' : ''}`}
                          style={{ height: hasUsage ? `${Math.max(heightPercent, 4)}%` : '3px' }}
                          onMouseEnter={(e) => handleBarMouseEnter(e, day, index)}
                          onMouseLeave={handleBarMouseLeave}
                        />
                        {index % Math.ceil(fullPeriodData.length / 7) === 0 && (
                          <span className="account-chart-label">{formatShortDate(day.date)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })()}
      </div>

      <BuyCreditsModal
        isOpen={showBuyCredits}
        onClose={() => setShowBuyCredits(false)}
        hasSubscription={!!subscription?.tier}
      />
    </AuthenticatedLayout>
  );
}

export default AccountPage;
